import { Download, File } from 'lucide-react'
import type React from 'react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { supabase } from '../../lib/supabase'
import { theme } from '../../theme/theme'
import type { ChatMessage as ChatMessageType } from '../../types/chat'
import { formatDistanceToNow } from '../../utils/dateUtils'

interface ChatMessageProps {
  message: ChatMessageType & {
    attachment_url?: string | null
    attachment_name?: string | null
    attachment_type?: string | null
    attachment_size?: number | null
  }
  isOwnMessage: boolean
  showTimestamp?: boolean
}

const MessageContainer = styled.div<{ isOwnMessage: boolean; showTimestamp: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.isOwnMessage ? 'flex-end' : 'flex-start')};
  margin-bottom: ${(props) => (props.showTimestamp ? theme.spacing.md : theme.spacing.xs)};
  max-width: 80%;
  align-self: ${(props) => (props.isOwnMessage ? 'flex-end' : 'flex-start')};
`

const MessageBubble = styled.div<{ isOwnMessage: boolean }>`
  background-color: ${({ theme, isOwnMessage }) => (isOwnMessage ? theme.colors.primaryDark : theme.colors.surface)};
  color: ${({ theme, isOwnMessage }) => (isOwnMessage ? theme.colors.text : theme.colors.text)};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  word-wrap: break-word;
`

const MessageTime = styled.span`
  font-size: ${theme.typography.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${theme.spacing.xs};
`

const AttachmentContainer = styled.div`
  margin-top: ${theme.spacing.xs};
  max-width: 300px;
`

const AttachmentImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`

const AttachmentFile = styled.a`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  background-color: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: ${theme.borderRadius.sm};
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text};
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.border};
  }
`

const AttachmentInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const AttachmentName = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const AttachmentSize = styled.div`
  font-size: ${theme.typography.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

// Utility function to format timestamp
const formatMessageTime = (timestamp: string): string => {
  try {
    return formatDistanceToNow(new Date(timestamp))
  } catch (error) {
    return 'Unknown time'
  }
}

// Utility function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// Check if attachment is an image
const isImage = (mimeType: string | null | undefined): boolean => {
  return mimeType?.startsWith('image/') ?? false
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message, isOwnMessage, showTimestamp = true }) => {
  const hasAttachment = message.attachment_url && message.attachment_name
  const [signedUrl, setSignedUrl] = useState<string | null>(null)
  const [loadingUrl, setLoadingUrl] = useState(false)

  // Generate signed URL for attachment
  useEffect(() => {
    if (!hasAttachment || !message.attachment_url) return

    const generateSignedUrl = async () => {
      setLoadingUrl(true)
      try {
        if (!message.attachment_url) {
          return
        }
        const { data, error } = await supabase.storage
          .from('chat-attachments')
          .createSignedUrl(message.attachment_url, 3600) // 1 hour expiry

        if (error) {
          console.error('Error generating signed URL:', error)
          return
        }

        setSignedUrl(data.signedUrl)
      } catch (err) {
        console.error('Error generating signed URL:', err)
      } finally {
        setLoadingUrl(false)
      }
    }

    generateSignedUrl()
  }, [hasAttachment, message.attachment_url])

  const handleDownload = async () => {
    if (!signedUrl || !message.attachment_name) return

    try {
      const response = await fetch(signedUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = message.attachment_name
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err) {
      console.error('Error downloading file:', err)
    }
  }

  return (
    <MessageContainer isOwnMessage={isOwnMessage} showTimestamp={showTimestamp}>
      <MessageBubble isOwnMessage={isOwnMessage}>
        {message.content}
        {hasAttachment && (
          <AttachmentContainer>
            {loadingUrl ? (
              <div style={{ padding: theme.spacing.sm, color: theme.colors.textSecondary }}>Loading attachment...</div>
            ) : signedUrl ? (
              isImage(message.attachment_type) ? (
                <AttachmentImage
                  src={signedUrl}
                  alt={message.attachment_name ?? 'Attachment'}
                  onClick={() => window.open(signedUrl, '_blank')}
                />
              ) : (
                <AttachmentFile as="button" onClick={handleDownload} style={{ width: '100%', textAlign: 'left' }}>
                  <File size={24} />
                  <AttachmentInfo>
                    <AttachmentName>{message.attachment_name}</AttachmentName>
                    {message.attachment_size && (
                      <AttachmentSize>{formatFileSize(message.attachment_size)}</AttachmentSize>
                    )}
                  </AttachmentInfo>
                  <Download size={18} />
                </AttachmentFile>
              )
            ) : (
              <div style={{ padding: theme.spacing.sm, color: theme.colors.textSecondary }}>
                Failed to load attachment
              </div>
            )}
          </AttachmentContainer>
        )}
      </MessageBubble>
      {showTimestamp && <MessageTime>{formatMessageTime(message.timestamp)}</MessageTime>}
    </MessageContainer>
  )
}
